import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Heart, Volume2, VolumeX, ChevronLeft, ChevronRight, Download, Share2, Edit, Crown, Sparkles, Users, Footprints, StopCircle, Music, Plus, Trash2, Globe, X, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { exportStoryAsPDF, shareStory } from '@/utils/pdfExport';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { canUseTextToSpeech, canUseOfflineReading, canEditStory, getFeatureLimitMessage } from '@/utils/subscriptionUtils';
import GuestModeBanner from '@/components/GuestModeBanner';

const LANGUAGES = [
  { code: 'es', name: 'Spanish (Español)' },
  { code: 'fr', name: 'French (Français)' },
  { code: 'de', name: 'German (Deutsch)' },
  { code: 'it', name: 'Italian (Italiano)' },
  { code: 'pt', name: 'Portuguese (Português)' },
  { code: 'zh', name: 'Chinese (中文)' },
  { code: 'ja', name: 'Japanese (日本語)' },
  { code: 'ru', name: 'Russian (Русский)' },
  { code: 'ar', name: 'Arabic (العربية)' },
  { code: 'hi', name: 'Hindi (हिन्दी)' },
];

const StoryReader = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const { user, token, nightMode } = useContext(AuthContext);
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [isReading, setIsReading] = useState(false);
  const [currentWord, setCurrentWord] = useState(-1);
  const [isSentenceReading, setIsSentenceReading] = useState(false);
  const [sentenceDelay, setSentenceDelay] = useState(2);
  const [currentSentenceRange, setCurrentSentenceRange] = useState(null);
  const [showSentenceSettings, setShowSentenceSettings] = useState(false);
  const [useNaturalVoice, setUseNaturalVoice] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [audioTimepoints, setAudioTimepoints] = useState([]);
  const audioTimepointsRef = useRef([]);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const sentenceReadingRef = useRef(false);
  const sentenceTimeoutRef = useRef(null);
  const startReadingTimeoutRef = useRef(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [editMoral, setEditMoral] = useState('');
  const [editPrompts, setEditPrompts] = useState([]);
  const [editVocabulary, setEditVocabulary] = useState([]);
  const [editPhonics, setEditPhonics] = useState([]);
  const [saving, setSaving] = useState(false);
  const [regeneratingImage, setRegeneratingImage] = useState(false);
  const [togglingCommunityShare, setTogglingCommunityShare] = useState(false);
  const utteranceRef = useRef(null);
  const audioRef = useRef(new Audio());
  const animationFrameIdRef = useRef(null);
  const [translation, setTranslation] = useState(null);
  const [targetLang, setTargetLang] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  
  const userTier = user?.subscription_tier || 'free';
  const isAdmin = user?.role === 'admin';
  const canTTS = canUseTextToSpeech(userTier) || isAdmin;
  const canDownload = canUseOfflineReading(userTier);
  const canEdit = canEditStory(userTier) || isAdmin;
  const canTranslate = userTier === 'family' || isAdmin;
  const isGuestMode = user?.id === 'guest-user';
  const isFolioTemplate = story?.is_folio_template === true;
  
  // Check if user owns this story
  const isOwner = story && user && story.user_id === user.id;

  useEffect(() => {
    fetchStory();
    
    // Load voices for better TTS support on mobile
    if ('speechSynthesis' in window) {
      // Trigger voice loading
      window.speechSynthesis.getVoices();
      // Some browsers need this event
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
      stopReading();
    };
  }, [storyId]);

  const fetchStory = async () => {
    try {
      const response = await axios.get(`${API}/stories/${storyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStory(response.data);
    } catch (error) {
      console.error('Failed to fetch story:', error);
      toast.error('Failed to load story');
      navigate('/library');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async () => {
    try {
      await axios.patch(
        `${API}/stories/${storyId}`,
        { is_favorite: !story.is_favorite },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStory({ ...story, is_favorite: !story.is_favorite });
      toast.success(story.is_favorite ? 'Removed from favorites' : 'Added to favorites');
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      toast.error('Failed to update favorite status');
    }
  };

  const speakWord = (word) => {
    if (isReading) {
      if (useNaturalVoice) {
        audioRef.current.pause();
        setIsReading(false);
      }
      stopReading();
    }
    
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const cleanWord = word.replace(/^[^\w]+|[^\w]+$/g, '');
      const text = cleanWord || word;
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.8;
      window.speechSynthesis.speak(utterance);
    }
  };

  const translateWord = async (word, langOverride = null) => {
    if (!canTranslate) {
      return;
    }

    const lang = langOverride || targetLang;

    if (!lang) {
      return;
    }

    setIsTranslating(true);
    setTranslation({ original: word, translated: '...' });

    try {
      const response = await axios.post(
        `${API}/tools/translate`,
        { text: word, target_lang: lang },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTranslation({ original: word, translated: response.data.translated_text });
    } catch (error) {
      console.error('Translation error:', error);
      setTranslation({ original: word, translated: 'Error' });
      toast.error(error.response?.data?.detail || 'Translation failed');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleWordClick = (word) => {
    // 1. Speak the word
    speakWord(word);
    
    // 2. Translate the word (if feature enabled)
    // Clean word for translation (remove punctuation)
    const cleanWord = word.replace(/^[^\w]+|[^\w]+$/g, '');
    if (cleanWord) translateWord(cleanWord);
  };

  const toggleReadAloud = () => {
    if (isReading) {
      stopReading();
    } else {
      startReading();
    }
  };

  // Handle time updates for natural voice highlighting
  const handleAudioTimeUpdate = () => {
    const timepoints = audioTimepointsRef.current;
    if (!audioRef.current || !timepoints.length) return;
    
    const currentTime = audioRef.current.currentTime;
    
    // Find the last timepoint that is <= current time
    // Timepoints from Google are in seconds
    let activeIndex = -1;
    for (let i = 0; i < timepoints.length; i++) {
      if (parseFloat(timepoints[i].timeSeconds) <= currentTime) {
        activeIndex = parseInt(timepoints[i].markName, 10);
      } else {
        break;
      }
    }
    
    if (activeIndex !== -1) {
      setCurrentWord(activeIndex);
    }
  };

  const playNaturalVoice = async () => {
    try {
      setIsLoadingAudio(true);
      console.log("Voice Source: Google Cloud TTS (Standard)");
      
      // Stop any browser speech
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      
      // Reset highlighting as we can't sync it with server audio
      setCurrentWord(-1);
      setIsSentenceReading(false);

      const response = await axios.get(`${API}/stories/${storyId}/narrate?speed=${playbackSpeed}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { audioContent, timepoints } = response.data;
      
      if (!audioContent) throw new Error("No audio content received");

      // Convert base64 to blob
      const byteCharacters = atob(audioContent);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'audio/mp3' });
      const url = URL.createObjectURL(blob);

      setAudioTimepoints(timepoints || []);
      audioTimepointsRef.current = timepoints || [];
      
      audioRef.current.src = url;
      audioRef.current.ontimeupdate = handleAudioTimeUpdate;
      audioRef.current.onended = () => setIsReading(false);
      audioRef.current.play();
      setIsReading(true);
    } catch (error) {
      console.error('Natural voice error:', error);
      toast.error('Failed to load natural voice. ' + (error.response?.status === 403 ? 'Admin access required.' : error.message));
      setUseNaturalVoice(false); // Fallback
    } finally {
      setIsLoadingAudio(false);
    }
  };

  const startReading = () => {
    if (useNaturalVoice) {
      playNaturalVoice();
      return;
    }

    console.log("Voice Source: Browser Speech Synthesis");

    if (!('speechSynthesis' in window)) {
      toast.error('Text-to-speech is not supported in your browser');
      return;
    }

    // Cancel any ongoing speech and reset state
    window.speechSynthesis.cancel();
    setCurrentWord(-1);
    
    // Also stop sentence reading if active
    sentenceReadingRef.current = false;
    setIsSentenceReading(false);
    setCurrentSentenceRange(null);
    if (sentenceTimeoutRef.current) clearTimeout(sentenceTimeoutRef.current);

    const textToRead = story.content;
    const utterance = new SpeechSynthesisUtterance(textToRead);
    
    // Try to use a high-quality voice
    const voices = window.speechSynthesis.getVoices();
    
    // Priority list of high-quality voices
    const preferredVoice = voices.find(voice => 
      voice.lang.startsWith('en') && (
        voice.name.includes('Natural') || 
        voice.name.includes('Neural')
      )
    ) || voices.find(voice => 
      (voice.name === 'Google US English' || 
       voice.name.includes('Microsoft Zira') ||
       voice.name === 'Samantha') && voice.lang.startsWith('en')
    ) || voices.find(voice => 
      voice.lang.startsWith('en') && (
        voice.name.includes('Google') || 
        voice.name.includes('Microsoft') || 
        voice.name.includes('Apple') ||
        voice.name.includes('Premium')
      )
    ) || voices.find(voice => voice.lang.startsWith('en'));
    
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    
    utterance.rate = playbackSpeed;
    utterance.pitch = 1.0;
    
    // Use onboundary event for highlighting
    utterance.onboundary = (event) => {
      setCurrentWord(event.charIndex);
    };

    utterance.onend = () => {
      setIsReading(false);
      setCurrentWord(-1);
    };
    
    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      setIsReading(false);
      setCurrentWord(-1);
    };
    
    utteranceRef.current = utterance;
    setIsReading(true);
    
    // Workaround for browser TTS cutting off start: play a silent buffer first
    const warmup = new SpeechSynthesisUtterance(" "); // Use a space which is silent
    warmup.volume = 0; // Make it completely inaudible
    warmup.rate = 10; // Make it finish as fast as possible
    if (preferredVoice) warmup.voice = preferredVoice;
    
    window.speechSynthesis.speak(warmup);
    window.speechSynthesis.speak(utterance);
  };

  const startSentenceReading = async () => {
    if (!('speechSynthesis' in window)) {
      toast.error('Text-to-speech is not supported in your browser');
      return;
    }
    
    stopReading(); // Stop standard reading if active
    
    setIsSentenceReading(true);
    sentenceReadingRef.current = true;
    setShowSentenceSettings(false);
    
    // Warmup for sentence reading
    const warmup = new SpeechSynthesisUtterance(".");
    warmup.volume = 0.1;
    warmup.rate = 2;
    window.speechSynthesis.speak(warmup);
    
    const text = story.content;
    let sentences = [];
    
    if (typeof Intl !== 'undefined' && Intl.Segmenter) {
        const segmenter = new Intl.Segmenter('en', { granularity: 'sentence' });
        for (const segment of segmenter.segment(text)) {
            sentences.push({
                text: segment.segment,
                start: segment.index,
                end: segment.index + segment.segment.length
            });
        }
    } else {
        const re = /[^.!?\n]+([.!?\n]+|$)/g;
        let match;
        while ((match = re.exec(text)) !== null) {
            if (match[0].trim().length > 0) {
                sentences.push({
                    text: match[0],
                    start: match.index,
                    end: match.index + match[0].length
                });
            }
        }
    }
    
    for (let i = 0; i < sentences.length; i++) {
        if (!sentenceReadingRef.current) break;
        
        const sentence = sentences[i];
        setCurrentSentenceRange({ start: sentence.start, end: sentence.end });
        
        await new Promise(resolve => {
            const utterance = new SpeechSynthesisUtterance(sentence.text);
            utterance.rate = playbackSpeed;
            
            // Use same voice logic as standard reading
            const voices = window.speechSynthesis.getVoices();
            const preferredVoice = voices.find(voice => 
              voice.lang.startsWith('en') && (voice.name.includes('Natural') || voice.name.includes('Neural'))
            ) || voices.find(voice => 
              (voice.name === 'Google US English' || voice.name.includes('Microsoft Zira') || voice.name === 'Samantha') && voice.lang.startsWith('en')
            ) || voices.find(voice => voice.lang.startsWith('en'));
            
            if (preferredVoice) utterance.voice = preferredVoice;

            utterance.onend = () => resolve();
            utterance.onerror = (e) => {
                console.error(e);
                resolve();
            };
            window.speechSynthesis.speak(utterance);
        });
        
        if (!sentenceReadingRef.current) break;
        
        // Wait delay
        if (i < sentences.length - 1) {
             await new Promise(resolve => {
                sentenceTimeoutRef.current = setTimeout(resolve, sentenceDelay * 1000);
            });
        }
    }
    
    stopSentenceReading();
  };

  const stopSentenceReading = () => {
    sentenceReadingRef.current = false;
    setIsSentenceReading(false);
    setCurrentSentenceRange(null);
    if (sentenceTimeoutRef.current) clearTimeout(sentenceTimeoutRef.current);
    if (window.speechSynthesis) window.speechSynthesis.cancel();
  };

  const stopReading = () => {
    if (startReadingTimeoutRef.current) {
      clearTimeout(startReadingTimeoutRef.current);
      startReadingTimeoutRef.current = null;
    }
    if (window.speechSynthesis) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.ontimeupdate = null;
        audioRef.current.currentTime = 0;
      }
      window.speechSynthesis.cancel();
    }
    setIsReading(false);
    setCurrentWord(-1);
    stopSentenceReading();
  };

  const handlePromptChange = (index, value) => {
    const newPrompts = [...editPrompts];
    newPrompts[index] = value;
    setEditPrompts(newPrompts);
  };
  const addPrompt = () => setEditPrompts([...editPrompts, '']);
  const removePrompt = (index) => setEditPrompts(editPrompts.filter((_, i) => i !== index));

  const handleVocabChange = (index, field, value) => {
    const newVocab = [...editVocabulary];
    newVocab[index] = { ...newVocab[index], [field]: value };
    setEditVocabulary(newVocab);
  };
  const addVocab = () => setEditVocabulary([...editVocabulary, { word: '', definition: '' }]);
  const removeVocab = (index) => setEditVocabulary(editVocabulary.filter((_, i) => i !== index));

  const handlePhonicsChange = (index, value) => {
    const newPhonics = [...editPhonics];
    newPhonics[index] = value;
    setEditPhonics(newPhonics);
  };
  const addPhonics = () => setEditPhonics([...editPhonics, '']);
  const removePhonics = (index) => setEditPhonics(editPhonics.filter((_, i) => i !== index));

  const openEditDialog = () => {
    setEditTitle(story.title);
    setEditContent(story.content);
    setEditSummary(story.summary || '');
    setEditMoral(story.moral || '');
    setEditPrompts(story.parent_prompts || []);
    setEditVocabulary(story.vocabulary || []);
    setEditPhonics(story.phonics || []);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const updateData = {
        title: editTitle,
        content: editContent,
        summary: editSummary,
        moral: editMoral,
        parent_prompts: editPrompts,
        vocabulary: editVocabulary,
        phonics: editPhonics
      };

      await axios.patch(
        `${API}/stories/${storyId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStory({ ...story, ...updateData });
      setEditDialogOpen(false);
      toast.success('Story updated successfully');
    } catch (error) {
      console.error('Failed to update story:', error);
      toast.error('Failed to update story');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateImage = async () => {
    if (isGuestMode) return;
    
    setRegeneratingImage(true);
    try {
      const response = await axios.post(
        `${API}/stories/${storyId}/regenerate-image`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStory(prev => ({ ...prev, images: [response.data.image] }));
      toast.success('Image regenerated successfully!');
    } catch (error) {
      console.error('Failed to regenerate image:', error);
      toast.error(error.response?.data?.detail || 'Failed to regenerate image');
    } finally {
      setRegeneratingImage(false);
    }
  };

  const handleToggleCommunityShare = async () => {
    if (isGuestMode || !isOwner) return;
    
    setTogglingCommunityShare(true);
    const newShareStatus = !story.community_shared;
    
    try {
      await axios.patch(
        `${API}/stories/${storyId}/community-share?share=${newShareStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStory(prev => ({ ...prev, community_shared: newShareStatus }));
      toast.success(newShareStatus 
        ? 'Story shared with community! An admin may feature it.' 
        : 'Story removed from community sharing'
      );
    } catch (error) {
      console.error('Failed to toggle community share:', error);
      toast.error(error.response?.data?.detail || 'Failed to update community sharing');
    } finally {
      setTogglingCommunityShare(false);
    }
  };

  const handleDownload = async () => {
    try {
      await exportStoryAsPDF(story);
      toast.success('Story downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download story');
    }
  };

  const handleShare = async () => {
    try {
      const result = await shareStory(story);
      if (result === 'copied') {
        toast.success('Story link copied to clipboard!');
      } else {
        toast.success('Story shared!');
      }
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Failed to share story');
    }
  };

  // Split content into pages
  const pages = [];
  if (story) {
    // Main story page
    pages.push({ 
      type: 'story', 
      content: story.content, 
      images: story.images || [], // This seems to be where the scene images are handled
      moral: story.moral,
      prompts: story.parent_prompts || [],
      vocabulary: story.vocabulary || [],
      phonics: story.phonics || []
    });
  }

  if (loading) {
    return (
      <div className={`min-h-screen ${nightMode ? 'night-gradient' : 'playful-gradient'} flex items-center justify-center`}>
        <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Loading story...</p>
      </div>
    );
  }

  if (!story) return null;

  const currentPageData = pages[currentPage];

  return (
    <div className={`min-h-screen ${nightMode ? 'night-gradient' : 'playful-gradient'} page-enter`}>
      {isGuestMode && <GuestModeBanner />}
      <div className={`container mx-auto px-4 sm:px-6 py-4 sm:py-6 ${canTranslate ? 'max-w-[1800px]' : 'max-w-7xl'}`}>
        {/* Header - Mobile Optimized */}
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-4 sm:mb-6">
            <Button
              data-testid="back-to-library-btn"
              onClick={() => navigate(-1)}
              variant="outline"
              className="rounded-full flex-shrink-0 w-20"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>

            {/* Edit - for owner with permission or admin */}
            {(isOwner || isAdmin) && !isFolioTemplate && (
              <Button
                data-testid="edit-story-btn"
                onClick={openEditDialog}
                disabled={isGuestMode || !canEdit}
                variant="outline"
                size="icon"
                className={`rounded-full relative flex-shrink-0 ${!canEdit || isGuestMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={isGuestMode ? "Sign in to edit stories" : canEdit ? "Edit story" : "Upgrade to Family or Folio tier to edit stories"}
              >
                <Edit className="w-4 h-4 sm:w-5 sm:h-5" />
                {!canEdit && !isGuestMode && <Crown className="absolute -top-1 -right-1 w-3 h-3" style={{ color: 'var(--accent-2)' }} />}
              </Button>
            )}
            {!isFolioTemplate && (
              <Button
                data-testid="share-story-btn"
                onClick={handleShare}
                variant="outline"
                size="icon"
                className="rounded-full flex-shrink-0"
                title="Share story"
              >
                <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            )}
            {/* Regenerate Image - only for owner or admin */}
            {(isOwner || isAdmin) && !isFolioTemplate && (
              <Button
                data-testid="regenerate-image-btn"
                onClick={handleRegenerateImage}
                disabled={isGuestMode || regeneratingImage}
                variant="outline"
                size="icon"
                className={`rounded-full flex-shrink-0 ${isGuestMode || regeneratingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={isGuestMode ? "Sign in to regenerate images" : "Regenerate Image (uses 1 credit)"}
              >
                <Sparkles className={`w-4 h-4 sm:w-5 sm:h-5 ${regeneratingImage ? 'animate-spin' : ''}`} />
              </Button>
            )}
            <Button
              data-testid="download-story-btn"
              onClick={handleDownload}
              variant="outline"
              size="icon"
              className={`rounded-full relative flex-shrink-0 ${!canDownload ? 'opacity-50' : ''}`}
              title={canDownload ? "Download/Print story" : "Upgrade to Starter tier for offline reading"}
            >
              <Download className="w-4 h-4 sm:w-5 sm:h-5" />
              {!canDownload && <Crown className="absolute -top-1 -right-1 w-3 h-3" style={{ color: 'var(--accent-2)' }} />}
            </Button>
            {isAdmin && (
              <Button
                onClick={() => setPlaybackSpeed(prev => prev === 1.0 ? 0.75 : 1.0)}
                variant="outline"
                size="icon"
                className="rounded-full relative flex-shrink-0"
                title={`Playback Speed: ${playbackSpeed}x`}
              >
                <span className="text-xs font-bold">{playbackSpeed}x</span>
              </Button>
            )}
            {user?.role === 'admin' && (
              <Button
                data-testid="toggle-voice-type-btn"
                onClick={() => setUseNaturalVoice(!useNaturalVoice)}
                variant="outline"
                size="icon"
                className={`rounded-full relative flex-shrink-0 ${useNaturalVoice ? 'bg-purple-100 border-purple-300 ring-2 ring-purple-200' : ''}`}
                title={useNaturalVoice ? "Using Natural Voice (Google Cloud)" : "Using Browser Voice"}
              >
                <Music className={`w-4 h-4 sm:w-5 sm:h-5 ${useNaturalVoice ? 'text-purple-600' : ''}`} />
              </Button>
            )}
            <Button
              data-testid="toggle-read-aloud-btn"
              onClick={toggleReadAloud}
              disabled={isGuestMode || !canTTS}
              variant="outline"
              size="icon"
              className={`rounded-full relative flex-shrink-0 ${!canTTS || isGuestMode ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={isGuestMode ? "Sign in to use text-to-speech" : canTTS ? "Read aloud" : "Upgrade to Starter tier for text-to-speech"}
            >
              {isReading ? <VolumeX className="w-4 h-4 sm:w-5 sm:h-5" /> : (isLoadingAudio ? <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" /> : <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" />)}
              {!canTTS && !isGuestMode && <Crown className="absolute -top-1 -right-1 w-3 h-3" style={{ color: 'var(--accent-2)' }} />}
            </Button>
            <Button
              data-testid="follow-me-reading-btn"
              onClick={() => {
                if (isSentenceReading) {
                  stopSentenceReading();
                } else {
                  setShowSentenceSettings(true);
                }
              }}
              disabled={isGuestMode || !canTTS}
              variant="outline"
              size="icon"
              className={`rounded-full relative flex-shrink-0 ${!canTTS || isGuestMode ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={isGuestMode ? "Sign in to use Follow Me Reading" : canTTS ? "Follow Me Reading" : "Upgrade to Starter tier for Follow Me Reading"}
            >
              {isSentenceReading ? <StopCircle className="w-4 h-4 sm:w-5 sm:h-5" /> : <Footprints className="w-4 h-4 sm:w-5 sm:h-5" />}
              {!canTTS && !isGuestMode && <Crown className="absolute -top-1 -right-1 w-3 h-3" style={{ color: 'var(--accent-2)' }} />}
            </Button>
            {/* Favorite - only for owner */}
            {isOwner && !isFolioTemplate && (
              <Button
                data-testid="toggle-favorite-btn"
                onClick={handleToggleFavorite}
                disabled={isGuestMode}
                variant="outline"
                size="icon"
                className={`rounded-full flex-shrink-0 ${isGuestMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={isGuestMode ? 'Sign in to favorite stories' : 'Toggle favorite'}
              >
                <Heart className="w-4 h-4 sm:w-5 sm:h-5" fill={story.is_favorite ? 'var(--accent-2)' : 'none'} />
              </Button>
            )}
            {/* Community Share - only for owner, paid tiers need to opt-in */}
            {isOwner && !isFolioTemplate && (
              <Button
                data-testid="toggle-community-share-btn"
                onClick={handleToggleCommunityShare}
                disabled={isGuestMode || togglingCommunityShare || userTier === 'free'}
                variant="outline"
                size="icon"
                className={`rounded-full flex-shrink-0 ${
                  story.community_shared ? 'ring-2 ring-green-500' : ''
                } ${isGuestMode || togglingCommunityShare ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={
                  userTier === 'free' 
                    ? 'Free tier stories are automatically eligible for community featuring' 
                    : story.community_shared 
                      ? 'Remove from community sharing' 
                      : 'Share with community (admin may feature)'
                }
                style={{
                  background: story.community_shared ? 'var(--accent-5)' : undefined,
                  color: story.community_shared ? 'white' : undefined
                }}
              >
                <Users className={`w-4 h-4 sm:w-5 sm:h-5 ${togglingCommunityShare ? 'animate-pulse' : ''}`} />
              </Button>
            )}
          </div>
        </div>

        {/* Main Content Box */}
        <div className="story-card p-2 sm:p-4" style={{ transform: 'none', transition: 'none' }}>
          <div className="flex flex-wrap items-center justify-center gap-2 mb-2 sm:mb-6 pb-2 border-b border-gray-100 dark:border-gray-800">
             <h1 className="text-xl md:text-2xl font-bold text-center" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
                {story.title}
              </h1>
              {/* Community Status Badges */}
              {story.admin_featured && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-500 text-white flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  <span className="hidden sm:inline">Featured in Community</span>
                </span>
              )}
              {story.community_shared && !story.admin_featured && userTier !== 'free' && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500 text-white flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  <span className="hidden sm:inline">Shared</span>
                </span>
              )}
              {userTier === 'free' && isOwner && !story.admin_featured && (
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--accent-1)', color: 'var(--text-secondary)' }}>
                  Eligible
                </span>
              )}
          </div>

          <div className="grid gap-4 items-start lg:grid-cols-12 lg:gap-8">
            {/* Left Column: Image & Questions & Translation */}
            <div className="lg:col-span-5 lg:sticky lg:top-6 space-y-4 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto pr-1">
               {/* Image */}
              {currentPageData.images && currentPageData.images.length > 0 && (
                <div className="rounded-2xl overflow-hidden">
                <img
                  src={`data:image/png;base64,${currentPageData.images[0]}`}
                  alt={story.title}
                  className="w-full"
                  style={{ maxHeight: '500px', objectFit: 'contain' }}
                />
                </div>
              )}

              {/* Discussion Questions (Desktop) */}
              {currentPageData.prompts && currentPageData.prompts.length > 0 && (
                <div className="hidden lg:block p-4 rounded-2xl bg-purple-50/50 dark:bg-gray-800/50 border border-purple-100 dark:border-gray-700">
                  <h3 className="text-xl sm:text-2xl font-bold mb-2" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--accent-2)' }}>
                    Discussion Questions
                  </h3>
                  <ul className="list-disc list-inside space-y-2">
                    {currentPageData.prompts.map((prompt, idx) => (
                      <li key={idx} className="text-base sm:text-lg leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                        {prompt}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Translation */}
              <div 
                className="hidden lg:block p-3 rounded-2xl border shadow-sm"
                style={{ 
                  background: nightMode ? 'var(--bg-secondary)' : 'white',
                  borderColor: 'var(--accent-2)'
                }}>
                <div className="flex flex-col gap-2 mb-4 lg:items-start">
                  <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5" style={{ color: 'var(--accent-2)' }} />
                    <span className="font-bold uppercase tracking-wider text-sm" style={{ color: 'var(--text-secondary)' }}>Translation</span>
                  </div>
                  
                  {!canTranslate && (
                    <p className="text-[10px] leading-tight">
                      <span className="font-bold text-amber-600">Family Feature: </span>
                      <button 
                        onClick={() => navigate('/subscription')}
                        className="font-bold underline text-amber-600 hover:text-amber-700"
                      >
                        Upgrade to unlock
                      </button>
                    </p>
                  )}

                  <select 
                    value={targetLang}
                    disabled={!canTranslate}
                    onChange={(e) => {
                      const newLang = e.target.value;
                      setTargetLang(newLang);
                      if (translation?.original) translateWord(translation.original, newLang);
                    }}
                    className={`text-xs p-1 rounded border bg-transparent w-full mt-1 ${!canTranslate ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                  >
                    <option value="" disabled>Select Language</option>
                    {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
                  </select>
                </div>

                <div className={!canTranslate ? 'opacity-30 pointer-events-none' : ''}>
                  {translation ? (
                    <div className="animate-in fade-in slide-in-from-top-2">
                      <div className="mb-2">
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{translation.original}</span>
                      </div>
                      <div>
                        <span className="text-2xl font-bold leading-tight" style={{ color: 'var(--accent-2)' }}>
                          {isTranslating ? <span className="animate-pulse">...</span> : translation.translated}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 opacity-50">
                      <p className="text-sm">Click any word in the story to see its translation here.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Story Text & Extras */}
            <div className="lg:col-span-7 space-y-4">
              {/* Story Text */}
              <div className="max-w-none" style={{ color: 'var(--text-primary)' }}>
                {(() => {
                  let currentGlobalIndex = 0;
                  return currentPageData.content.split('\n').map((paragraph, idx) => {
                    const paragraphLength = paragraph.length;
                    if (!paragraph.trim()) {
                      currentGlobalIndex += paragraphLength + 1;
                      return <div key={idx} className="h-6" />;
                    }

                    const words = [];
                    const regex = /\S+/g;
                    let match;
                    while ((match = regex.exec(paragraph)) !== null) {
                      words.push({
                        text: match[0],
                        index: match.index,
                        length: match[0].length
                      });
                    }

                    const elements = [];
                    let lastIndex = 0;

                    words.forEach((wordObj, wIdx) => {
                      if (wordObj.index > lastIndex) {
                        elements.push(paragraph.substring(lastIndex, wordObj.index));
                      }

                      const globalStart = currentGlobalIndex + wordObj.index;
                      const isWordHighlighted = isReading && currentWord >= globalStart && currentWord < (globalStart + wordObj.length);
                      const isSentenceHighlighted = isSentenceReading && currentSentenceRange && globalStart >= currentSentenceRange.start && globalStart < currentSentenceRange.end;
                      
                      const isHighlighted = isWordHighlighted || isSentenceHighlighted;

                      elements.push(
                        <span
                          key={`w-${idx}-${wIdx}`}
                          className={`cursor-pointer rounded px-0.5 transition-colors ${
                            isHighlighted 
                              ? (isSentenceHighlighted ? 'bg-blue-200 text-black font-medium shadow-sm' : 'bg-yellow-400 text-black font-medium shadow-sm')
                              : (nightMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100')
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleWordClick(wordObj.text);
                          }}
                          title="Click to read"
                        >
                          {wordObj.text}
                        </span>
                      );

                      lastIndex = wordObj.index + wordObj.length;
                    });

                    if (lastIndex < paragraph.length) {
                      elements.push(paragraph.substring(lastIndex));
                    }

                    currentGlobalIndex += paragraphLength + 1;

                    return (
                      <p key={idx} className="mb-0 text-xl lg:text-xl leading-[1.1]">
                        {elements}
                      </p>
                    );
                  });
                })()}
              </div>

              {/* Moral */}
              {currentPageData.moral && (
                <div className="mt-4 sm:mt-6 p-3 sm:p-4 rounded-xl" style={{ background: 'var(--accent-4)', border: '2px solid var(--sketch-border)' }}>
                  <p className="font-bold text-base sm:text-lg" style={{ color: 'var(--text-primary)' }}>
                    <strong>Moral:</strong> {currentPageData.moral}
                  </p>
                </div>
              )}

              {/* Additional Content Sections */}
              {/* Parent Prompts (Mobile Only) */}
              {currentPageData.prompts && currentPageData.prompts.length > 0 && (
                <div className="lg:hidden p-4 rounded-2xl bg-purple-50/50 dark:bg-gray-800/50 border border-purple-100 dark:border-gray-700">
                  <h3 className="text-xl sm:text-2xl font-bold mb-2" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--accent-2)' }}>
                    Discussion Questions
                  </h3>
                  <ul className="list-disc list-inside space-y-2">
                    {currentPageData.prompts.map((prompt, idx) => (
                      <li key={idx} className="text-base sm:text-lg" style={{ color: 'var(--text-primary)' }}>
                        {prompt}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Vocabulary Words */}
              {currentPageData.vocabulary && currentPageData.vocabulary.length > 0 && (
                <div className="pt-6 border-t border-gray-100 dark:border-gray-800">
                  <h3 className="text-xl sm:text-2xl font-bold mb-4" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--accent-2)' }}>
                    Vocabulary Words
                  </h3>
                  <div className="grid gap-2">
                    {currentPageData.vocabulary.map((vocabItem, idx) => (
                      <div
                        key={idx}
                        className="p-2 sm:p-3 rounded-lg cursor-pointer"
                        style={{ background: 'var(--accent-4)', border: '2px solid var(--sketch-border)' }}
                        onClick={() => {
                          const word = vocabItem.word || vocabItem;
                          handleWordClick(word);
                        }}
                      >
                      <p className="font-bold text-lg sm:text-xl" style={{ color: 'var(--accent-2)' }}>{vocabItem.word}</p>
                      <p className="text-base sm:text-lg mt-0.5" style={{ color: 'var(--text-primary)' }}>{vocabItem.definition}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Phonics Words */}
              {currentPageData.phonics && currentPageData.phonics.length > 0 && (
                <div className="pt-6 border-t border-gray-100 dark:border-gray-800">
                  <h3 className="text-xl sm:text-2xl font-bold mb-4" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--accent-2)' }}>
                    Phonics Practice
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {currentPageData.phonics.map((phonicsItem, idx) => {
                      // Extract just the word from "Word: Pattern" format
                      const wordOnly = phonicsItem.includes(':') 
                        ? phonicsItem.split(':')[0].trim() 
                        : phonicsItem;
                      
                      return (
                        <div
                          key={idx}
                          className="p-2 sm:p-3 rounded-xl text-center cursor-pointer"
                          style={{ background: 'var(--accent-3)', color: 'white' }}
                          onClick={() => {
                            handleWordClick(wordOnly);
                          }}
                        >
                        <p className="text-lg sm:text-xl">{phonicsItem}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Translation (Mobile Only) */}
              <div className="lg:hidden pt-6 border-t border-gray-100 dark:border-gray-800">
                <div className="p-3 rounded-2xl border shadow-sm" 
                  style={{ 
                    background: nightMode ? 'var(--bg-secondary)' : 'white',
                    borderColor: 'var(--accent-2)'
                    }}>
                  <div className="flex flex-col gap-2 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Globe className="w-5 h-5" style={{ color: 'var(--accent-2)' }} />
                        <span className="font-bold uppercase tracking-wider text-sm" style={{ color: 'var(--text-secondary)' }}>Translation</span>
                      </div>
                      {!canTranslate && (
                        <div className="text-[10px] leading-tight text-right">
                          <span className="font-bold text-amber-600">Family Feature: </span>
                          <button 
                            onClick={() => navigate('/subscription')}
                            className="font-bold underline text-amber-600"
                          >
                            Upgrade
                          </button>
                        </div>
                      )}
                    </div>

                    <select 
                      value={targetLang}
                      disabled={!canTranslate}
                      onChange={(e) => {
                        const newLang = e.target.value;
                        setTargetLang(newLang);
                        if (translation?.original) translateWord(translation.original, newLang);
                      }}
                      className={`text-xs p-1 rounded border bg-transparent w-full ${!canTranslate ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                    >
                      <option value="" disabled>Select Language</option>
                      {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
                    </select>
                  </div>

                  <div className={!canTranslate ? 'opacity-30 pointer-events-none' : ''}>
                    {translation ? (
                      <div className="animate-in fade-in slide-in-from-top-2">
                        <div className="mb-2">
                          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{translation.original}</span>
                        </div>
                        <div>
                          <span className="text-2xl font-bold leading-tight" style={{ color: 'var(--accent-2)' }}>
                            {isTranslating ? <span className="animate-pulse">...</span> : translation.translated}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 opacity-50">
                        <p className="text-sm">Click any word in the story to see its translation here.</p>
                      </div>
                    )}
                  </div>
                    </div>
              </div>
            </div>

          </div>
     

        {(
        <div className="mt-12 mb-20 text-center story-card p-8">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
            Create your own stories!
          </h2>
          <p className="text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
            Generate personalized, educational bedtime at <a href="https://dreamcanvasmaker.com" className="font-bold hover:underline">https://dreamcanvasmaker.com</a>
          </p>
          <Button
            onClick={() => window.location.href = '/'}
            className="pill-button h-auto whitespace-normal py-3"
            style={{ background: 'var(--accent-2)', color: 'white' }}
          >
            <BookOpen className="mr-2 w-5 h-5" />
            <div className="flex flex-col items-center leading-tight">
              <span className="text-lg">Try StoryTime Studio</span>
              <span className="text-xs font-normal opacity-90">by Dream Canvas Maker</span>
            </div>
          </Button>
          {userTier === 'free' && (
            <p className="mt-4 text-xs opacity-70" style={{ color: 'var(--text-secondary)' }}>
              Note: Free accounts are deleted after 60 days of inactivity.
            </p>
          )}
        </div>
        )}
      </div>

      {/* Edit Story Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Edit Story</DialogTitle>
            <DialogDescription className="font-sketch">
              Make changes to your story&apos;s title and content
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="edit-title" className="font-sketch font-semibold">Story Title</Label>
              <Input
                id="edit-title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="edit-content" className="font-sketch font-semibold">Story Content</Label>
              <Textarea
                id="edit-content"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                rows={15}
                className="mt-2 font-sketch"
              />
            </div>
            <div>
              <Label htmlFor="edit-summary" className="font-sketch font-semibold">Summary</Label>
              <Textarea
                id="edit-summary"
                value={editSummary}
                onChange={(e) => setEditSummary(e.target.value)}
                rows={3}
                className="mt-2 font-sketch"
              />
            </div>
            <div>
              <Label htmlFor="edit-moral" className="font-sketch font-semibold">Moral</Label>
              <Input
                id="edit-moral"
                value={editMoral}
                onChange={(e) => setEditMoral(e.target.value)}
                className="mt-2"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="font-sketch font-semibold">Discussion Questions</Label>
                <Button type="button" onClick={addPrompt} variant="ghost" size="sm" className="h-6 w-6 p-0"><Plus className="h-4 w-4" /></Button>
              </div>
              {editPrompts.map((prompt, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Input value={prompt} onChange={(e) => handlePromptChange(index, e.target.value)} />
                  <Button type="button" onClick={() => removePrompt(index)} variant="ghost" size="icon" className="shrink-0"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="font-sketch font-semibold">Vocabulary</Label>
                <Button type="button" onClick={addVocab} variant="ghost" size="sm" className="h-6 w-6 p-0"><Plus className="h-4 w-4" /></Button>
              </div>
              {editVocabulary.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Input value={item.word} onChange={(e) => handleVocabChange(index, 'word', e.target.value)} placeholder="Word" className="w-1/3" />
                  <Input value={item.definition} onChange={(e) => handleVocabChange(index, 'definition', e.target.value)} placeholder="Definition" className="w-2/3" />
                  <Button type="button" onClick={() => removeVocab(index)} variant="ghost" size="icon" className="shrink-0"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-sketch font-semibold">Phonics</Label>
                  <Button type="button" onClick={addPhonics} variant="ghost" size="sm" className="h-6 w-6 p-0"><Plus className="h-4 w-4" /></Button>
                </div>
                {editPhonics.map((item, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input value={item} onChange={(e) => handlePhonicsChange(index, e.target.value)} placeholder="e.g. Cat: Short 'a' sound" />
                    <Button type="button" onClick={() => removePhonics(index)} variant="ghost" size="icon" className="shrink-0"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => setEditDialogOpen(false)}
                variant="outline"
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={saving}
                className="pill-button"
                style={{ background: 'var(--accent-2)', color: 'white' }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sentence Reading Settings Dialog */}
      <Dialog open={showSentenceSettings} onOpenChange={setShowSentenceSettings}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Follow Me Reading</DialogTitle>
            <DialogDescription className="font-sketch">
              Read the story one sentence at a time with a pause.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="delay-input" className="font-sketch font-semibold">Pause between sentences (seconds)</Label>
              <Input
                id="delay-input"
                type="number"
                min="0"
                max="10"
                value={sentenceDelay}
                onChange={(e) => setSentenceDelay(Number(e.target.value))}
                className="mt-2"
              />
            </div>
            <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowSentenceSettings(false)}>Cancel</Button>
                <Button onClick={startSentenceReading} className="pill-button" style={{ background: 'var(--accent-2)', color: 'white' }}>Start Reading</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoryReader;
